import { useState, useEffect, useCallback } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { Copy, Check, Wifi, WifiOff, Users, ChevronRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useGameSocket } from "@/hooks/useGameSocket";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MIN_PLAYERS = 11;
const MIN_PITCHERS = 2;

function isTeamReady(team) {
  if (!team?.players) return false;
  const pitchers = team.players.filter(p => p.position === "P");
  const batters = team.players.filter(p => p.position !== "P");
  return team.players.length >= MIN_PLAYERS && pitchers.length >= MIN_PITCHERS && batters.length >= 9;
}

function PlayerCount({ team, isOwn }) {
  const total = team?.players?.length || 0;
  const pitchers = team?.players?.filter(p => p.position === "P").length || 0;
  const batters = team?.players?.filter(p => p.position !== "P").length || 0;
  const ready = isTeamReady(team);

  return (
    <div className={`flex items-center gap-3 px-3 py-2 rounded-lg border text-xs font-mono transition-colors ${
      ready ? "border-emerald-500/50 bg-emerald-500/10" : "border-slate-700 bg-slate-900/40"
    }`}>
      <span className={ready ? "text-emerald-400 font-bold" : "text-slate-400"}>
        {total}/{MIN_PLAYERS} players
      </span>
      <span className="text-slate-600">·</span>
      <span className={pitchers >= MIN_PITCHERS ? "text-emerald-400" : "text-slate-500"}>
        {pitchers}/{MIN_PITCHERS} P
      </span>
      <span className="text-slate-600">·</span>
      <span className={batters >= 9 ? "text-emerald-400" : "text-slate-500"}>
        {batters}/9 bat
      </span>
      {ready && <Check size={12} className="text-emerald-400 ml-auto" />}
    </div>
  );
}

function RosterList({ team }) {
  const players = team?.players || [];
  if (!players.length) return (
    <p className="text-xs text-slate-600 italic text-center py-6">No players yet</p>
  );

  const pitchers = players.filter(p => p.position === "P");
  const batters = players.filter(p => p.position !== "P");

  return (
    <div className="space-y-0.5 max-h-60 overflow-y-auto pr-1">
      {pitchers.map(p => (
        <div key={p.id} className="flex items-center gap-2 px-2 py-1 rounded">
          <span className="text-[9px] font-bold text-red-400 w-5 flex-shrink-0">P</span>
          <span className="text-xs text-slate-300 truncate">{p.name}</span>
          {p.year && <span className="text-[9px] text-slate-600 ml-auto">{p.year}</span>}
        </div>
      ))}
      {batters.map((p, i) => (
        <div key={p.id} className="flex items-center gap-2 px-2 py-1 rounded">
          <span className="text-[9px] font-bold text-slate-500 w-5 flex-shrink-0">{p.position}</span>
          <span className="text-xs text-slate-300 truncate">{p.name}</span>
          {p.year && <span className="text-[9px] text-slate-600 ml-auto">{p.year}</span>}
        </div>
      ))}
    </div>
  );
}

function ConnectionDot({ connected, reconnecting, label }) {
  return (
    <div className="flex items-center gap-1.5">
      <div className={`w-2 h-2 rounded-full ${connected ? "bg-emerald-400 animate-pulse" : reconnecting ? "bg-amber-400 animate-pulse" : "bg-slate-600"}`} />
      <span className="text-[10px] text-slate-500">{label}</span>
      {reconnecting
        ? <Loader2 size={10} className="text-amber-400 animate-spin" />
        : connected
          ? <Wifi size={10} className="text-emerald-400" />
          : <WifiOff size={10} className="text-slate-600" />}
    </div>
  );
}

export default function OnlineLobby() {
  const { gameId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const role = searchParams.get("role") === "away" ? "away" : "home";
  const isHome = role === "home";

  const [game, setGame] = useState(null);
  const [homeReady, setHomeReady] = useState(false);
  const [awayReady, setAwayReady] = useState(false);
  const [copied, setCopied] = useState(false);
  const [starting, setStarting] = useState(false);

  const awayUrl = `${window.location.origin}/online/${gameId}?role=away`;

  const handleMessage = useCallback((msg) => {
    if (msg.type === "game_state") {
      setGame(msg.game);
    } else if (msg.type === "team_ready") {
      if (msg.role === "home") setHomeReady(msg.ready);
      if (msg.role === "away") setAwayReady(msg.ready);
    } else if (msg.type === "game_start") {
      navigate(`/game/${gameId}?role=${role}`);
    }
  }, [gameId, navigate, role]);

  const { send, connected, reconnecting, homeConnected, awayConnected } = useGameSocket(gameId, role, handleMessage);

  useEffect(() => {
    // Fetch initial state via REST (WS delivers updates)
    axios.get(`${API}/games/${gameId}`).then(r => setGame(r.data)).catch(() => toast.error("Couldn't load game — try refreshing"));
  }, [gameId]);

  const handleCopy = () => {
    navigator.clipboard.writeText(awayUrl);
    setCopied(true);
    toast.success("Away link copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  const myTeam = isHome ? game?.home_team : game?.away_team;
  const myReady = isHome ? homeReady : awayReady;
  const myReadyState = myReady || isTeamReady(myTeam);

  const handleToggleReady = () => {
    const next = !myReadyState;
    if (isHome) setHomeReady(next); else setAwayReady(next);
    send({ type: "team_ready", ready: next });
  };

  const bothReady = (homeReady || isTeamReady(game?.home_team)) &&
                    (awayReady || isTeamReady(game?.away_team)) &&
                    homeConnected && awayConnected;

  const handleStart = () => {
    if (!bothReady) return;
    setStarting(true);
    send({ type: "game_start" });
    // Navigation is handled by the WS `game_start` broadcast received in handleMessage.
    // Fallback: if the WS event doesn't arrive within 8s (e.g. slow connection), navigate anyway.
    setTimeout(() => navigate(`/game/${gameId}?role=${role}`), 8000);
  };

  if (!game) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-5 h-5 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 sm:p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-black tracking-tight text-white">Head to Head (Online)</h1>
          <p className="text-xs text-slate-500 mt-0.5">
            You are <span className={`font-bold ${isHome ? "text-amber-400" : "text-blue-400"}`}>
              {isHome ? "HOME" : "AWAY"}
            </span>
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <ConnectionDot connected={homeConnected} reconnecting={isHome && reconnecting} label="Home" />
          <ConnectionDot connected={awayConnected} reconnecting={!isHome && reconnecting} label="Away" />
        </div>
      </div>

      {/* Share link (home only) */}
      {isHome && (
        <div className="mb-6 p-4 rounded-xl border border-slate-700 bg-slate-900/60">
          <p className="text-xs text-slate-400 mb-2 font-semibold uppercase tracking-wider">Share with Away Player</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-[10px] text-amber-400 bg-slate-800 px-3 py-2 rounded-lg truncate font-mono">
              {awayUrl}
            </code>
            <button
              onClick={handleCopy}
              className="flex-shrink-0 p-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-black transition-colors"
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
            </button>
          </div>
          {!awayConnected && (
            <p className="text-[10px] text-slate-600 mt-2">Waiting for away player to connect…</p>
          )}
        </div>
      )}

      {/* Away player — waiting banner */}
      {!isHome && !homeConnected && (
        <div className="mb-6 p-4 rounded-xl border border-slate-700 bg-slate-900/60 text-center">
          <p className="text-xs text-slate-400">Waiting for home player to connect…</p>
        </div>
      )}

      {/* Rosters */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {[
          { key: "away", team: game.away_team, label: "Away Team", color: "blue", isOwn: !isHome },
          { key: "home", team: game.home_team, label: "Home Team", color: "amber", isOwn: isHome },
        ].map(({ key, team, label, color, isOwn }) => (
          <div key={key} className={`rounded-xl border p-4 space-y-3 ${
            isOwn ? "border-slate-600 bg-slate-900/80" : "border-slate-700/50 bg-slate-900/40"
          }`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-xs font-black uppercase tracking-widest ${color === "amber" ? "text-amber-400" : "text-blue-400"}`}>
                  {label}
                </p>
                <p className="text-sm font-bold text-white mt-0.5">{team?.name}</p>
              </div>
              {isOwn && (
                <span className="text-[9px] bg-slate-700 text-slate-400 px-2 py-0.5 rounded font-bold uppercase tracking-wider">
                  You
                </span>
              )}
            </div>

            <PlayerCount team={team} isOwn={isOwn} />
            <RosterList team={team} />

            {isOwn && (
              <a
                href={`/setup/${gameId}`}
                className="flex items-center justify-center gap-1.5 w-full py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-xs font-semibold text-white transition-colors border border-slate-600"
                onClick={(e) => {
                  e.preventDefault();
                  // Pass back role so setup can notify WS on changes
                  navigate(`/setup/${gameId}?role=${role}&online=1`);
                }}
              >
                <Users size={12} />
                Edit Roster
                <ChevronRight size={12} />
              </a>
            )}
          </div>
        ))}
      </div>

      {/* Ready + Start */}
      <div className="flex flex-col sm:flex-row items-center gap-3">
        <button
          onClick={handleToggleReady}
          disabled={!isTeamReady(myTeam)}
          className={`flex-1 py-3 rounded-xl font-black text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed ${
            myReadyState
              ? "bg-emerald-500 hover:bg-emerald-400 text-black"
              : "bg-slate-800 hover:bg-slate-700 text-white border border-slate-600"
          }`}
        >
          {myReadyState ? "✓ Ready!" : isTeamReady(myTeam) ? "Mark as Ready" : `Need ${MIN_PLAYERS} players + ${MIN_PITCHERS} pitchers`}
        </button>

        {isHome && (
          <button
            onClick={handleStart}
            disabled={!bothReady || starting}
            className="flex-1 py-3 rounded-xl font-black text-sm bg-amber-500 hover:bg-amber-400 text-black transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {starting ? "Starting…" : bothReady ? "Start Game →" : "Waiting for both teams…"}
          </button>
        )}

        {!isHome && bothReady && (
          <div className="flex-1 py-3 rounded-xl text-center text-sm font-bold text-emerald-400 border border-emerald-500/30 bg-emerald-500/10">
            Both ready — waiting for home to start
          </div>
        )}
      </div>
    </div>
  );
}
